import * as RadixDialog from '@radix-ui/react-dialog';
import { __ } from '@wordpress/i18n';
import { useId } from 'react';

/**
 * Canonical Dialog primitive for sb-customizer.
 *
 * Wraps @radix-ui/react-dialog and provides:
 *   - role="dialog" + aria-modal
 *   - Focus trap on open
 *   - Focus restore on close
 *   - Escape to close
 *   - Click-outside to close
 *   - Scroll lock on body
 *   - aria-labelledby wired to <Title>
 *
 * Consumers (ModalContainer, Modals/*, TikTokPlugin/Feed/Elements/Modal.jsx)
 * should use this in place of ad-hoc <section>/<div> dialog implementations.
 *
 * For composition that needs to preserve existing markup, use the lower-level
 * exports (DialogTrigger, DialogClose, RadixDialog.*) with the asChild pattern.
 *
 * Reference: .pitch/wcag-accessibility-audit/react-canonical-pattern.md (PR 1).
 */
export function Dialog({
    open,
    onOpenChange,
    title,
    describedBy,
    size = 'md',
    hideCloseButton = false,
    closeIcon,
    closeLabel,
    className = '',
    container,
    children,
}) {
    const titleId = useId();
    const resolvedCloseLabel = closeLabel || __('Close', 'sb-customizer');

    return (
        <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
            <RadixDialog.Portal container={container}>
                <RadixDialog.Overlay className="sb-dialog-overlay" />
                <RadixDialog.Content
                    className={`sb-dialog sb-dialog--${size} ${className}`.trim()}
                    aria-labelledby={titleId}
                    aria-describedby={describedBy}
                >
                    <RadixDialog.Title id={titleId} className="sb-dialog__title">
                        {title}
                    </RadixDialog.Title>
                    {children}
                    {!hideCloseButton && (
                        <RadixDialog.Close asChild>
                            <button
                                type="button"
                                className="sb-dialog__close sb-button-reset"
                                aria-label={resolvedCloseLabel}
                            >
                                {closeIcon || (
                                    <svg
                                        aria-hidden="true"
                                        focusable="false"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 14 14"
                                    >
                                        <path
                                            d="M1 1L13 13M13 1L1 13"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                )}
                            </button>
                        </RadixDialog.Close>
                    )}
                </RadixDialog.Content>
            </RadixDialog.Portal>
        </RadixDialog.Root>
    );
}

export const DialogRoot = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogPortal = RadixDialog.Portal;
export const DialogOverlay = RadixDialog.Overlay;
export const DialogContent = RadixDialog.Content;
export const DialogTitle = RadixDialog.Title;
export const DialogDescription = RadixDialog.Description;
export const DialogClose = RadixDialog.Close;
